// src/pages/po/PreOrdemItemsSidePanelContent.tsx
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClockRotateLeft, faFileInvoiceDollar } from '@/icons';
import axios from 'axios';
import ScrollableTable from '../../components/common/ScrollableTable';
import IconSearchSupplierButton from '../../components/common/IconSearchSupplierButton';
import IconCartAddButton from '../../components/common/IconCartAddButton';
import CloseButtonSidePanel from '../../components/common/CloseButtonSidePanel';
import ReturnButton from '../../components/common/ReturnButton';
import GiveUpButton from '../../components/common/GiveUpButton';
import SaveButton from '../../components/common/SaveButton';
import Modal from '../../components/common/Modal';
import SearchSupplierModalContent from './SearchSupplierModalContent';
import HistoricoItemModalContent from './HistoricoItemModalContent';
import CompraHistoricoModalContent from './CompraHistoricoModalContent';
import { usePaginatedFetch } from '../../hooks/usePaginatedFetch';
import { apiGet, apiPut } from '../../services/api';
import { MENSAGEM_TRAVA_ATIVA, resolveLoteError } from '../../services/loteError';
import { PreOrdemItem, PreOrdemResponse } from '../../types/compras/Compra';
import { HistoricoResponse, CompraHistoricoResponse } from '../../types/compras/Historico';
import './PreOrdemItemsSidePanelContent.css';
import SearchProductModalContent from '../../components/common/SearchProductModalContent';
import { formatDate, formatDateTime } from '../../utils/formatUtils';
import { useApiCall } from '../../hooks/useApiCall';
import { useOperacaoLote } from '@/hooks/useOperacaoLote';
import { chaveLote, useOperacoesLote } from '@/contexts/OperacaoLoteContext';
import {
  contarSelecionados,
  fragmentoSelectAll,
  mensagemResultado,
  textoSelecao,
  type ModoSelecao,
} from '@/utils/selectAll';
import { useAuth } from '../../contexts/AuthContext';
import { showAlert } from '../../contexts/DialogContext';
import { Toast } from '../../contexts/ToastContext';
import { logger } from '@/utils/logger';

// Exigida pelo backend apenas quando o conjunto ESVAZIA a cotacao (cascata que
// cancela a pre-ordem). Ver PreOrdemService:379.
const PERMISSAO_CANCELAR = 'gestao_compras.cancelar_cotacoes';

// Uma string so para os dois pontos que barram a saturacao — o aviso no momento
// da selecao e o guard do Salvar. Duas copias divergiriam com o tempo.
const AVISO_SATURACAO =
  'Você não tem permissão para cancelar a cotação, e remover todos os itens a cancelaria. Desmarque ao menos um item.';

// Texto espelhado de PreOrdemItem.devolver, no backend.
//
// Assimetria conhecida e ACEITA entre os modos: no enumerado, quantidade zero
// vai ao servidor, volta 400, e o catch de `handleSave` mostra a generica
// "Erro ao devolver os itens. Tente novamente." — comportamento historico, que
// esta frente nao altera (premissa #9). No modo 'todos' o override e barrado
// ANTES de sair, com este texto, porque um unico item zerado derrubaria o lote
// inteiro (a acao e tudo-ou-nada) e a causa ficaria invisivel.
//
// O efeito observavel e o mesmo nos dois — a acao e recusada, nada e gravado —,
// e a diferenca esta na precisao da mensagem, nunca no que vai para o banco.
const AVISO_QTD_ZERO = 'Quantidade a devolver deve ser maior que zero.';

interface PreOrdemHeader {
  dataCriacao: string;
  status: string;
  nomeUsuario: string;
}

interface PreOrdemItemsSidePanelContentProps {
  preOrdem: { id: number };
  theme: 'dark' | 'light';
  onClose: () => void;
  onDataChange?: () => void; // Nova prop opcional
  showCheckboxesInitially?: boolean;
}

const PreOrdemItemsSidePanelContent: React.FC<PreOrdemItemsSidePanelContentProps> = ({
  preOrdem,
  theme,
  onClose,
  onDataChange,
  showCheckboxesInitially = false,
}) => {
  const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false);
  const [isHistoricoModalOpen, setIsHistoricoModalOpen] = useState(false);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [historicoData, setHistoricoData] = useState<HistoricoResponse | null>(null);
  const [compraHistoricoData, setCompraHistoricoData] = useState<CompraHistoricoResponse | null>(null);
  const [isCompraHistoricoModalOpen, setIsCompraHistoricoModalOpen] = useState(false);
  const [header, setHeader] = useState<PreOrdemHeader | null>(null);
  const [showReturnMode, setShowReturnMode] = useState(false);
  const [showCheckboxes, setShowCheckboxes] = useState(showCheckboxesInitially);
  const [selectedItems, setSelectedItems] = useState<PreOrdemItem[]>([]);
  const [returnQuantities, setReturnQuantities] = useState<{ [key: number]: number }>({});
  const [selectionResetKey, setSelectionResetKey] = useState(0);
  const { callApi, isLoading: isSaving } = useApiCall();
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // --- "Selecionar tudo entre paginas" (issue #31, superficie 3) -------------
  // Vale SO para o modo de remocao. Os checkboxes desta tela servem a DOIS
  // fluxos: "Buscar Fornecedor" (que aplica o fornecedor apenas aos itens
  // carregados, via handleSupplierUpdate) e "Remover". O modo 'todos' so tem
  // contrato no segundo — no primeiro nao existe select-all nenhum do lado do
  // servidor. Por isso a separacao e por `showReturnMode` em todos os pontos,
  // nao so na prop da tabela.
  const { hasPermission } = useAuth();
  const podeCancelarCotacao = hasPermission(PERMISSAO_CANCELAR);
  const [modoSelecao, setModoSelecao] = useState<ModoSelecao>('enumerado');
  const [excludeIds, setExcludeIds] = useState<Set<number>>(new Set());
  const { disparar, estaProcessando } = useOperacaoLote();
  const { aoConcluir } = useOperacoesLote();

  // Espelha a trava (acao, chave_pai) do backend. REMOVER e o valor do enum
  // AcaoLote, nao o rotulo "Remover" do botao.
  const chaveTrava = chaveLote('REMOVER', preOrdem.id);
  const processandoLote = estaProcessando(chaveTrava);

  const fetchItems = useCallback(async (page: number, _currentItems: PreOrdemItem[]) => {
    const response = await apiGet(`/v1/pre-ordens/${preOrdem.id}/itens?page=${page}&size=10`);
    const data: PreOrdemResponse = response.data;
    const items = data.content[0]?.itens.content || [];
    const totalItems = data.content[0]?.itens.page.totalElements || 0; // Alterado para acessar o caminho correto da resposta da API, resolvendo undefined e permitindo paginação
    if (data.content[0]) {
      setHeader({
        dataCriacao: data.content[0].dataCriacao,
        status: data.content[0].status,
        nomeUsuario: data.content[0].nomeUsuario,
      });
    }
    return { items, totalItems };
  }, [preOrdem.id]);

  const { items, totalItems, hasMore, isLoading, loadMore, updateItems, reset } = usePaginatedFetch<PreOrdemItem>({
    fetchFunction: fetchItems,
    pageSize: 10,
    uniqueKey: 'id',
    initialPage: 0,
  });

  useEffect(() => {
    buttonRefs.current = items.map((_, i) => buttonRefs.current[i] || null);
  }, [items]);

  const handleSearchSupplierClick = () => {
    setIsSupplierModalOpen(true);
    setShowCheckboxes(true); // Ativa checkboxes ao abrir o modal
  };

  const handleSupplierModalClose = () => {
    setIsSupplierModalOpen(false);
    // Mantém selectedItems, mas ajusta showCheckboxes com base na seleção
    setShowCheckboxes(showCheckboxesInitially || selectedItems.length > 0);
  };

  const handleTestModalOpen = () => {
    setIsTestModalOpen(true);
  };

  const handleTestModalClose = () => {
    setIsTestModalOpen(false);
  };

  const handleSupplierUpdate = ({
    codFornecedor,
    nomeFornecedor,
    dataEntrega,
    countDiasEntrega
  }: {
    codFornecedor: number;
    nomeFornecedor: string;
    dataEntrega: string;
    countDiasEntrega: number;
  }) => {
    logger.log('Fornecedor selecionado:', { codFornecedor, nomeFornecedor, dataEntrega, countDiasEntrega });
    updateItems((prevItems) =>
      prevItems.map((item) =>
        selectedItems.some((selected) => selected.id === item.id)
          ? { ...item, codFornecedor, nomeFornecedor, dataEntrega, countDiasEntrega }
          : item
      )
    );
    setSelectedItems([]); // Limpa as seleções após atualizar os itens
    setSelectionResetKey((k) => k + 1);
    setShowCheckboxes(false); // Oculta os checkboxes após a atualização
    handleSupplierModalClose();
    onDataChange?.(); // Sinaliza alteração de dados
  };

const handleViewHistoryClick = async (item: PreOrdemItem, _index: number) => {
  try {
    // Adiciona parâmetros obrigatórios codProduto e codCategoriaProduto, e itemModuloId como opcional
    const queryParams = new URLSearchParams({
      codProduto: item.codProduto,
      codCategoriaProduto: item.codCategoriaProduto.toString(),
      itemModuloId: item.requisicaoItemId.toString(),
      page: '0',
      size: '10',
    });
    const response = await apiGet(`/v1/historico-itens?${queryParams.toString()}`);
    setHistoricoData(response.data);
    setSelectedItemId(item.requisicaoItemId);
    setIsHistoricoModalOpen(true);
  } catch (err) {
    logger.error('Erro ao carregar histórico:', err);
    if (axios.isAxiosError(err) && err.response?.status === 400 && err.response?.data?.error) {
      await showAlert(err.response.data.error, { variant: 'error' });
    } else {
      await showAlert('Erro ao carregar histórico. Tente novamente.', { variant: 'error' });
    }
  }
};

  const handleCloseHistoricoModal = () => {
    setIsHistoricoModalOpen(false);
    setHistoricoData(null);
    setSelectedItemId(null);
  };

  const handleCompraHistoricoClick = async (item: PreOrdemItem) => {
    try {
      if (!item.codProduto || !item.codCategoriaProduto) {
        await showAlert('Dados do item incompletos. Verifique o produto e a categoria.', { variant: 'warning' });
        return;
      }
      const queryParams = new URLSearchParams({
        codProduto: item.codProduto,
        codCategoriaProduto: item.codCategoriaProduto.toString(),
        page: '0',
        size: '10',
      });
      const response = await apiGet(`/v1/historico-compras-produto?${queryParams.toString()}`);
      setCompraHistoricoData(response.data);
      setIsCompraHistoricoModalOpen(true);
    } catch (err) {
      logger.error('Erro ao carregar histórico de compras do produto:', err);
      if (axios.isAxiosError<{ error?: string }>(err) && err.response?.status === 400 && err.response?.data?.error) {
        await showAlert(err.response.data.error, { variant: 'error' });
      } else {
        await showAlert('Erro ao carregar o histórico de compras. Tente novamente.', { variant: 'error' });
      }
    }
  };

  const handleCloseCompraHistoricoModal = () => {
    setIsCompraHistoricoModalOpen(false);
    setCompraHistoricoData(null);
  };

  const handleReturnClick = () => {
    setShowReturnMode(true);
    setShowCheckboxes(true);
  };

  // Volta ao estado neutro da remocao. Extraida porque os tres caminhos que
  // encerram o modo (desistir, sucesso inline, conclusao do lote) repetiam a
  // mesma sequencia — e uma copia que esquecesse de zerar excludeIds deixaria
  // uma exclusao invisivel valendo na proxima remocao.
  const limparEstadoRemocao = () => {
    setShowReturnMode(false);
    setShowCheckboxes(showCheckboxesInitially);
    setSelectedItems([]);
    setReturnQuantities({});
    setSelectionResetKey((k) => k + 1);
    setModoSelecao('enumerado');
    setExcludeIds(new Set());
  };

  const handleGiveUpClick = () => {
    limparEstadoRemocao();
  };

  // --- Selecao: convencao de checkbox de cabecalho de tabela ------------------
  //   vazio   -> clicar marca a fila inteira (modo 'todos');
  //   traco   -> parte selecionada; clicar limpa;
  //   marcado -> tudo selecionado; clicar limpa.
  //
  // Esta tela NAO passa renderCheckbox: todo item carregado e selecionavel, e
  // por isso nao existe aqui o estado "bloqueado visivel" que leva o cabecalho a
  // traco nas telas de aprovacao/reversao.

  const limparSelecao = () => {
    setModoSelecao('enumerado');
    setExcludeIds(new Set());
    setSelectedItems([]);
  };

  const entrarEmTodos = () => {
    setModoSelecao('todos');
    setExcludeIds(new Set());
    setSelectedItems([]);
    // Sem exclusao, o conjunto esvazia a cotacao — e o backend so entao exige a
    // segunda permissao (PreOrdemService:379), validada ANTES de resolver. A
    // permissao depende do TAMANHO da selecao, nao da acao, entao o aviso vem no
    // gesto: deixar o 400 explicar seria o oposto do que esta frente vem fazendo.
    if (!podeCancelarCotacao) {
      void showAlert(AVISO_SATURACAO, { variant: 'warning' });
    }
  };

  const handleSelecionarItem = (item: PreOrdemItem, marcado: boolean) => {
    if (modoSelecao === 'todos') {
      // Desmarcar ALIMENTA a lista de exclusao — e o que permite dizer "todos
      // menos este" sem o navegador conhecer todos.
      setExcludeIds((prev) => {
        const next = new Set(prev);
        if (marcado) next.delete(item.id);
        else next.add(item.id);
        return next;
      });
      return;
    }

    const proximos = marcado
      ? [...selectedItems, item]
      : selectedItems.filter((it) => it.id !== item.id);

    // Marcar a mao todas as linhas carregadas equivale a marcar o cabecalho, e o
    // cabecalho significa a fila inteira (regra do dev, superficie 1).
    if (marcado && items.length > 0 && proximos.length === items.length) {
      entrarEmTodos();
      return;
    }
    setSelectedItems(proximos);
  };

  const estadoCabecalho: 'vazio' | 'parcial' | 'marcado' =
    modoSelecao === 'todos'
      ? excludeIds.size === 0
        ? 'marcado'
        : 'parcial'
      : selectedItems.length === 0
        ? 'vazio'
        : 'parcial';

  const handleToggleCabecalho = () => {
    if (estadoCabecalho === 'vazio') entrarEmTodos();
    else limparSelecao();
  };

  // TETO, nao promessa (ADR-030 item 3). O total vem da LISTAGEM, e o resolver
  // ainda descarta item ja vinculado a ordem de compra e item sem saldo (a
  // materializacao, DEC-004). A mensagem de fim nunca repete este numero.
  const qtdSelecionada = contarSelecionados(
    modoSelecao,
    totalItems,
    modoSelecao === 'enumerado' ? selectedItems.length : excludeIds.size,
  );

  const temSelecao = modoSelecao === 'todos' ? qtdSelecionada > 0 : selectedItems.length > 0;

  // O conjunto cobre a cotacao inteira — o gatilho da cascata que a cancela.
  const selecaoSatura = modoSelecao === 'todos' && excludeIds.size === 0;

  // Linhas visiveis que o usuario editou a mao. Vao como `overrides`, aplicados
  // POR CIMA do default do servidor (qtdProduto + qtdProdutoExtra). O que ele
  // digitou e o que grava; o que ele nao tocou fica com o default — que nesta
  // tela e o MESMO numero que a coluna "Quantidade Total" exibe, porque a
  // entidade mantem qtdProdutoOriginal = qtdProduto + qtdProdutoExtra como
  // invariante (PreOrdemItem.java:85, :110, :145).
  const overridesEditados = () =>
    items
      .filter((it) => !excludeIds.has(it.id) && returnQuantities[it.id] !== undefined)
      .map((it) => ({ preOrdemItemId: it.id, quantidadeDevolver: returnQuantities[it.id] }));

  const handleSave = async () => {
    if (selectedItems.length === 0) return;

    const payload = {
      itens: selectedItems.map(item => ({
        preOrdemItemId: item.id,
        quantidadeDevolver: returnQuantities[item.id] ?? quantidadeDevolverPadrao(item),
      })),
    };

    try {
      await callApi('put', `/v1/pre-ordens/${preOrdem.id}/devolver-itens`, payload, {
        onSuccess: () => {
          Toast.success('Itens devolvidos com sucesso!');

          reset();

          limparEstadoRemocao();
          onDataChange?.();
        },
      });
    } catch (err) {
      logger.error('Erro ao devolver itens:', err);
      await showAlert('Erro ao devolver os itens. Tente novamente.', { variant: 'error' });
    }
    // Removido try/finally: gerenciado pelo hook.
  };

  // Fim do caminho INLINE do modo complemento. O assincrono nao passa por aqui:
  // quem avisa e o OperacaoLoteProvider, porque o usuario pode ja ter saido.
  //
  // Sem contadores: o 200 desta rota devolve a CAPA da pre-ordem, nao decisao
  // por item, e a acao e tudo-ou-nada (um item invalido reverte o lote). Nao ha
  // o soft-fail por item que faz contadoresInline() existir na aprovacao e na
  // reversao — aqui, ou processou tudo, ou veio 400.
  const finalizarInline = () => {
    const msg = mensagemResultado({ assincrono: false });
    limparEstadoRemocao();
    onDataChange?.();
    reset();
    Toast.success(msg.texto);
  };

  const dispararSelectAll = async () => {
    if (selecaoSatura && !podeCancelarCotacao) {
      await showAlert(AVISO_SATURACAO, { variant: 'warning' });
      return;
    }

    const overrides = overridesEditados();
    // O backend recusa override com quantidade <= 0 (400, tudo-ou-nada). Barrar
    // aqui evita derrubar o lote inteiro por uma linha zerada — e descartar o
    // override em silencio seria pior: gravaria o default sobre um numero que o
    // usuario ve zerado na tela.
    if (overrides.some((o) => o.quantidadeDevolver <= 0)) {
      await showAlert(AVISO_QTD_ZERO, { variant: 'warning' });
      return;
    }

    const payload = {
      ...fragmentoSelectAll<never, number>({ excludeIds: [...excludeIds] }),
      ...(overrides.length > 0 ? { overrides } : {}),
    };

    try {
      const resultado = await disparar({
        chaveTrava,
        executar: () => apiPut(`/v1/pre-ordens/${preOrdem.id}/devolver-itens`, payload),
        onInline: finalizarInline,
      });

      if (resultado === 'bloqueado') {
        // Mesma frase da recusa vinda do servidor (ver o catch abaixo): para o
        // usuario e a mesma situacao, e quem detectou a trava — esta aba ou o
        // servidor — e detalhe de implementacao.
        await showAlert(MENSAGEM_TRAVA_ATIVA, { variant: 'warning' });
        return;
      }

      // Aceito para segundo plano: o provider ja avisou e vai avisar de novo no
      // fim, onde quer que o usuario esteja. A lista NAO e relida agora — quem
      // recarrega e a inscricao em aoConcluir, se o painel ainda estiver aberto.
      if (resultado === 'aceito') {
        limparEstadoRemocao();
        onDataChange?.();
      }
    } catch (err) {
      logger.error('Erro ao remover itens da cotação:', err);
      // A mensagem do backend passa adiante: e ela que distingue "sem permissao
      // para cancelar" (400) de "cotacao ja cancelada ou confirmada" (409).
      // A excecao e a recusa da trava de lote — um 400 que NAO e falha, e sim
      // espera: o helper a separa e ela sai como aviso, nao como erro.
      const alerta = resolveLoteError(err, 'Erro ao remover os itens. Tente novamente.');
      await showAlert(alerta.message, { variant: alerta.variant });
    }
  };

  // Recarrega a lista quando um lote DESTE pai termina, se o painel ainda
  // estiver aberto. O handler vive num ref para a inscricao nao ser refeita a
  // cada render.
  const aoConcluirLoteRef = useRef<() => void>(() => {});
  aoConcluirLoteRef.current = () => {
    // A lista mudou: os itens removidos sairam. Qualquer selecao pendente aponta
    // para um conjunto que nao existe mais, entao ela e sempre zerada — inclusive
    // a do fluxo "Buscar Fornecedor", que compartilha `selectedItems` com a
    // remocao e mandaria ids ja removidos ao aplicar o fornecedor.
    //
    // O que NAO se mexe e o MODO em que o usuario esta. Chamar
    // limparEstadoRemocao() aqui fecharia o modo e esconderia os checkboxes por
    // baixo de quem estava no fluxo de fornecedor — a mesma forma do defeito que
    // a review da superficie 2 achou na outra tela (um lote de uma acao mexendo
    // no estado de outra).
    if (showReturnMode) {
      limparEstadoRemocao();
    } else {
      setSelectedItems([]);
      setSelectionResetKey((k) => k + 1);
    }
    onDataChange?.();
    reset();
  };

  useEffect(() => aoConcluir(chaveTrava, () => aoConcluirLoteRef.current()), [aoConcluir, chaveTrava]);

  // Canal de selecao INTERNA da tabela. So dispara fora do modo de remocao —
  // ou seja, no fluxo "Buscar Fornecedor", onde a selecao continua sendo a
  // historica (pagina carregada, comparacao por referencia).
  const handleSelectChange = (newSelectedItems: PreOrdemItem[]) => {
    setSelectedItems(newSelectedItems);
    // Garante que os checkboxes permaneçam visíveis se houver itens selecionados
    if (newSelectedItems.length > 0) {
      setShowCheckboxes(true);
    }
  };

  const normalizeQuantityInput = (rawValue: string) => {
    const normalized = rawValue.replace(',', '.');
    if (!normalized || normalized === '.') return 0;
    const [integerPartRaw, decimalPartRaw = ''] = normalized.split('.');
    const integerPart = integerPartRaw === '' ? '0' : integerPartRaw;
    const decimalPart = decimalPartRaw.slice(0, 3);
    const sanitized = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
    const parsed = Number.parseFloat(sanitized);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  // Quantidade que o campo do modo devolver assume quando o usuario nao digita
  // nada: a QUANTIDADE TOTAL do item — a mesma que a coluna "Quantidade Total"
  // exibe, e o mesmo valor que `handleQuantityChange` usa como teto.
  //
  // Fonte UNICA de proposito. Este valor e lido em dois lugares — o `value` do
  // input e o fallback do payload — e ate aqui a expressao estava repetida nos
  // dois. Repetida, ela permite que uma linha nao tocada MOSTRE um numero e
  // ENVIE outro, sem erro nenhum. Alterar aqui muda os dois de uma vez.
  const quantidadeDevolverPadrao = (item: PreOrdemItem): number => item.qtdProdutoOriginal;

  const handleQuantityChange = (itemId: number, value: string) => {
    const maxQuantity = items.find((item: PreOrdemItem) => item.id === itemId)?.qtdProdutoOriginal || 0;
    let numValue = normalizeQuantityInput(value);
    if (numValue > 0 && numValue < 0.001) numValue = 0.001;
    if (numValue > maxQuantity) numValue = maxQuantity;
    numValue = Number(numValue.toFixed(3));
    setReturnQuantities((prev) => ({
      ...prev,
      [itemId]: numValue,
    }));
  };

  const columns = [
    {
      header: 'Código',
      render: (item: PreOrdemItem) => item.codProduto,
    },
    {
      header: 'Cód. Categoria',
      render: (item: PreOrdemItem) => item.codCategoriaProduto,
    },
    {
      header: 'Nome do Produto',
      render: (item: PreOrdemItem) => item.nomeProduto,
    },
    {
      header: 'Categoria',
      render: (item: PreOrdemItem) => item.nomeCategoria,
    },
    {
      header: 'Quantidade Compra',
      render: (item: PreOrdemItem) => item.qtdProduto,
    },
    {
      header: 'Quantidade Extra',
      render: (item: PreOrdemItem) => item.qtdProdutoExtra,
    },
    {
      header: 'Quantidade Total',
      render: (item: PreOrdemItem) => item.qtdProdutoOriginal,
    },
    {
      header: 'Prioridade',
      render: (item: PreOrdemItem) => {
        const prioridade = item.prioridade || 'Não definida';
        if (prioridade === 'URGENTE') {
          return <span className="urgent-priority">{prioridade}</span>;
        }
        return prioridade;
      },
    },
    {
      header: 'Requisição',
      render: (item: PreOrdemItem) => item.requisicaoId,
    },
    {
      header: 'Loja',
      render: (item: PreOrdemItem) => item.nomeFantasiaLoja,
    },
    {
      header: 'Solicitante',
      render: (item: PreOrdemItem) => item.nomePessoaUsuario,
    },
    {
      header: 'Fornecedor',
      render: (item: PreOrdemItem) => item.nomeFornecedor ?? 'Não informado',
    },
    {
      header: 'Entrega',
      render: (item: PreOrdemItem) => (item.dataEntrega ? formatDate(item.dataEntrega) : 'Não informada'),
    },
    {
      header: 'Faltam',
      render: (item: PreOrdemItem) =>
        item.countDiasEntrega != null ? `${item.countDiasEntrega} dias` : 'Não informado',
    },      
  ];

  const returnColumns = showReturnMode
    ? [
        {
          header: 'Quantidade',
          render: (item: PreOrdemItem) => (
            <input
              type="number"
              min={0.001}
              max={item.qtdProdutoOriginal}
              step={0.001}
              value={returnQuantities[item.id] ?? quantidadeDevolverPadrao(item)}
              onChange={(e) => handleQuantityChange(item.id, e.target.value)}
              className="quantity-input"
            />
          ),
          width: '100px',
        },
      ]
    : [];

  return (
    <div className={`pre-ordem-items-sidepanel-content ${theme}`}>
      <CloseButtonSidePanel onClick={onClose} className="close-button-absolute" disabled={isLoading || isSaving} />
      <div className="title-table">
        <div>
          <div className="facelet-h3">
            <h3>
              Cotação Nº {preOrdem.id} | Criada em{' '}
              {header ? formatDateTime(header.dataCriacao) : 'Carregando...'}
            </h3>
          </div>
          <div className="facelet-p">
            <p>
              Comprador(a): {header?.nomeUsuario || 'Carregando...'} | Status: {' '}
              {header?.status || 'Carregando...'}
            </p>
          </div>
        </div>
        <div className='right-facelet'>
          <div>
            <IconCartAddButton
              onClick={handleTestModalOpen}
              title="Adicionar Itens"
              className="icon-cart-add-button"
            />
          </div>
          <div>
            <IconSearchSupplierButton
              onClick={handleSearchSupplierClick}
              title="Buscar Fornecedor"
              className="icon-search-supplier-button"
              disabled={selectedItems.length === 0} // Desabilita se nenhum item selecionado
            />
          </div>
        </div>
      </div>
      <ScrollableTable<PreOrdemItem>
        data={items}
        hasMore={hasMore}
        loadMore={loadMore}
        isLoading={isLoading}
        columns={[...columns, ...returnColumns]}
        onSelectChange={handleSelectChange}
        renderHistoryAction={(item, index) => (
          <button
            className="action-button"
            ref={(el) => {
              buttonRefs.current[index] = el;
            }}
            onClick={() => handleViewHistoryClick(item, index)}
            title="Ver Histórico do Item"
          >
            <FontAwesomeIcon icon={faClockRotateLeft} />
          </button>
        )}
        renderPurchaseHistoryAction={(item) => (
          <button
            className="action-button"
            onClick={() => handleCompraHistoricoClick(item)}
            title="Ver Histórico de Compras"
          >
            <FontAwesomeIcon icon={faFileInvoiceDollar} />
          </button>
        )}
        emptyMessage="Nenhum item encontrado."
        showCheckboxes={showCheckboxes}
        selectionResetKey={selectionResetKey}
        // Opt-in SO no modo de remocao. Ausente no fluxo "Buscar Fornecedor",
        // que aplica o fornecedor apenas aos itens carregados e nao tem
        // contrato de select-all no servidor — e prop ausente e, por
        // construcao, o caminho historico do ScrollableTable.
        selecaoEntrePaginas={
          showReturnMode
            ? {
                estadoCabecalho,
                estaSelecionado: (it: PreOrdemItem) =>
                  modoSelecao === 'todos'
                    ? !excludeIds.has(it.id)
                    : selectedItems.some((s) => s.id === it.id),
                onToggleCabecalho: handleToggleCabecalho,
                onSelecionarItem: handleSelecionarItem,
              }
            : undefined
        }
      />
      <div className="form-actions">
        {!showReturnMode ? (
          <ReturnButton onClick={handleReturnClick} disabled={isLoading || isSaving} label="Remover" />
        ) : (
          <>
            {/* Escopo da selecao. Informativo — o gesto e o checkbox do cabecalho. */}
            {estadoCabecalho !== 'vazio' && (
              <span className="remocao-info-selecao">
                {textoSelecao(qtdSelecionada, totalItems)}
              </span>
            )}
            <GiveUpButton
              onClick={handleGiveUpClick}
              disabled={isLoading || isSaving || processandoLote}
            />
            {temSelecao && (
              <SaveButton
                isLoading={isSaving || processandoLote}
                onClick={modoSelecao === 'todos' ? dispararSelectAll : handleSave}
                disabled={isLoading || isSaving || processandoLote}
              />
            )}
          </>
        )}
      </div>
      <Modal
        isOpen={isSupplierModalOpen}
        onClose={handleSupplierModalClose}
        theme={theme}
        zIndex={1001}
      >
        <SearchSupplierModalContent
          ordemCompraId={preOrdem.id}
          theme={theme}
          onClose={handleSupplierModalClose}
          onSupplierUpdate={handleSupplierUpdate}
          selectedItemIds={selectedItems.map(item => item.id)} // Passa IDs dos itens selecionados
        />
      </Modal>
      {isHistoricoModalOpen && historicoData && (
        <Modal
          isOpen={isHistoricoModalOpen}
          onClose={handleCloseHistoricoModal}
          theme={theme}
          zIndex={1002}
        >
          <HistoricoItemModalContent
            historico={historicoData}
            requisicaoItemId={selectedItemId!}
            isOpen={isHistoricoModalOpen}
            onClose={handleCloseHistoricoModal}
            theme={theme}
            showCancelButton={false}
          />
        </Modal>
      )}
      {isCompraHistoricoModalOpen && compraHistoricoData && (
        <Modal
          isOpen={isCompraHistoricoModalOpen}
          onClose={handleCloseCompraHistoricoModal}
          theme={theme}
          zIndex={1002}
        >
          <CompraHistoricoModalContent
            compraHistorico={compraHistoricoData}
            isOpen={isCompraHistoricoModalOpen}
            onClose={handleCloseCompraHistoricoModal}
            theme={theme}
            showCancelButton={false}
          />
        </Modal>
      )}
        <Modal
          isOpen={isTestModalOpen}
          onClose={handleTestModalClose}
          theme={theme}
          zIndex={1003}
        >
          <SearchProductModalContent
            preOrdemId={preOrdem.id}
            theme={theme}
            onClose={handleTestModalClose}
            tipo={['COMPRA']}
            status={['PENDENTE', 'ABERTA']}
            onItemsAdded={() => {
              reset(); // Recarrega a tabela de itens da pré-ordem
              onDataChange?.(); // Sinaliza alteração para recarregar "Requisições Pendentes"
            }}
          />
        </Modal>
    </div>
  );
};

export default PreOrdemItemsSidePanelContent;
